/* ===========================================================================
   DILLON PRECISION — RELOADING CALCULATORS
   Pure functions. No dependencies. Each returns null when inputs are unusable,
   so the UI can show a placeholder instead of NaN.

   Every formula below is derived from the fields on the existing JotForm
   calculators. They are pending verification by a reloader — see the
   VERIFY notes in the harness.
   =========================================================================== */
var DPC = (function () {
  'use strict';

  var GRAINS_PER_POUND = 7000;
  var INCHES_PER_YARD  = 36;
  var MOA_AT_100YD_IN  = 1.047;   /* true MOA, not the 1.000" "shooter's MOA" */
  var MIL_AT_100YD_IN  = 3.6;

  function num(v) {
    if (v === null || v === undefined) return NaN;
    var n = parseFloat(String(v).replace(/[$,\s]/g, ''));
    return isFinite(n) ? n : NaN;
  }
  function pos(v) { var n = num(v); return n > 0 ? n : NaN; }

  /* --- 1 · POWER FACTOR -------------------------------------------------
     PF = bullet weight (grains) x velocity (fps) / 1000                    */
  function powerFactor(grains, fps) {
    var w = pos(grains), v = pos(fps);
    if (isNaN(w) || isNaN(v)) return null;
    return { pf: (w * v) / 1000, grains: w, fps: v };
  }

  /* --- 2 · SIGHT CORRECTION --------------------------------------------
     Iron sights, by similar triangles:
       movement = (sight radius x miss distance) / distance to target
     with every term in the same unit. Distance is converted to inches.

     Rear sight moves the SAME direction you want the impact to move.
     Front sight moves the OPPOSITE direction.                              */
  function sightIron(missIn, distance, distanceUnit, radiusIn, direction) {
    var m = pos(missIn), d = pos(distance), r = pos(radiusIn);
    if (isNaN(m) || isNaN(d) || isNaN(r)) return null;
    var distIn = distanceUnit === 'yd' ? d * INCHES_PER_YARD : d;
    if (distIn <= 0) return null;

    /* impact must move opposite to the miss */
    var want = { low: 'UP', high: 'DOWN', left: 'RIGHT', right: 'LEFT' }[direction];
    var opposite = { UP: 'DOWN', DOWN: 'UP', LEFT: 'RIGHT', RIGHT: 'LEFT' };

    return {
      movement: (r * m) / distIn,
      distIn: distIn,
      rear: want,
      front: opposite[want],
      axis: (direction === 'low' || direction === 'high') ? 'vertical' : 'horizontal'
    };
  }

  /* Scopes and red dots: angular, so sight radius is irrelevant.
       MOA = miss / (1.047 x yards / 100)
       MIL = miss / (3.6   x yards / 100)                                   */
  function sightOptic(missIn, distance, distanceUnit, clickValue, direction) {
    var m = pos(missIn), d = pos(distance);
    if (isNaN(m) || isNaN(d)) return null;
    var yards = distanceUnit === 'yd' ? d : d / INCHES_PER_YARD;
    if (yards <= 0) return null;

    var moa = m / (MOA_AT_100YD_IN * yards / 100);
    var mil = m / (MIL_AT_100YD_IN * yards / 100);
    var cv  = parseFloat(clickValue);
    var isMil = clickValue === '0.1MIL';
    var clicks = isMil ? mil / 0.1 : moa / cv;

    return {
      moa: moa, mil: mil, clicks: clicks, yards: yards,
      dial: { low: 'UP', high: 'DOWN', left: 'RIGHT', right: 'LEFT' }[direction],
      unitLabel: isMil ? '0.1 MIL' : cv + ' MOA'
    };
  }

  /* --- 3 · TAPER CRIMP --------------------------------------------------
     Case mouth diameter = bullet diameter + (2 x case wall thickness) - crimp
     Two wall thicknesses because the case surrounds the bullet on both sides.
     Taper crimp only. Does NOT apply to roll-crimped revolver cartridges.   */
  var CRIMP_LEVELS = [
    { key: '0.000', amount: 0.000, label: 'Neck tension only' },
    { key: '0.001', amount: 0.001, label: 'Very light crimp' },
    { key: '0.002', amount: 0.002, label: 'Light crimp' },
    { key: '0.003', amount: 0.003, label: 'Moderate crimp' },
    { key: '0.004', amount: 0.004, label: 'Heavy crimp' }
  ];
  function crimp(bulletDia, wallThickness) {
    var b = pos(bulletDia), w = pos(wallThickness);
    if (isNaN(b) || isNaN(w)) return null;
    var base = b + (2 * w);
    return {
      base: base,
      rows: CRIMP_LEVELS.map(function (l) {
        return { key: l.key, label: l.label, amount: l.amount, mouth: base - l.amount };
      })
    };
  }

  /* --- 4 · RELOADING COST ----------------------------------------------
     Per round:
       projectile = price / qty
       brass      = (price / qty) / times the brass will be reloaded
       primer     = price / qty
       powder     = price x charge / (pounds x 7000)                        */
  function reloadingCost(i) {
    var pq = pos(i.projQty),  pp = num(i.projPrice);
    var bq = pos(i.brassQty), bp = num(i.brassPrice);
    var rq = pos(i.primerQty), rp = num(i.primerPrice);
    var wl = pos(i.powderLb), cw = pos(i.charge), wp = num(i.powderPrice);
    var uses = pos(i.brassUses); if (isNaN(uses)) uses = 1;

    if ([pq, bq, rq, wl, cw].some(isNaN)) return null;
    if ([pp, bp, rp, wp].some(function (n) { return isNaN(n) || n < 0; })) return null;

    var proj   = pp / pq;
    var brass  = (bp / bq) / uses;
    var primer = rp / rq;
    var powder = (wp * cw) / (wl * GRAINS_PER_POUND);
    var per    = proj + brass + primer + powder;

    var out = {
      proj: proj, brass: brass, primer: primer, powder: powder,
      per: per, per100: per * 100, per1000: per * 1000,
      roundsPerPound: GRAINS_PER_POUND / cw,
      brassUses: uses
    };

    /* Optional comparison against factory ammunition */
    var fp = num(i.factoryPrice), fc = pos(i.factoryCount);
    if (!isNaN(fp) && fp > 0 && !isNaN(fc)) {
      var factoryPer = fp / fc;
      out.factory = {
        per: factoryPer,
        savingPer: factoryPer - per,
        saving100: (factoryPer - per) * 100,
        saving1000: (factoryPer - per) * 1000,
        pct: factoryPer > 0 ? ((factoryPer - per) / factoryPer) * 100 : 0
      };
    }
    return out;
  }

  return {
    powerFactor: powerFactor,
    sightIron: sightIron,
    sightOptic: sightOptic,
    crimp: crimp,
    crimpLevels: CRIMP_LEVELS,
    reloadingCost: reloadingCost,
    _const: { GRAINS_PER_POUND: GRAINS_PER_POUND, MOA_AT_100YD_IN: MOA_AT_100YD_IN, MIL_AT_100YD_IN: MIL_AT_100YD_IN }
  };
})();


/* ===========================================================================
   UI wiring. Reads DPC (core.js), writes results. One IIFE per calculator so
   a page that only contains one of them costs nothing for the other three.
   Every calculator recalculates on input — there is no submit button.
   =========================================================================== */
(function () {
  'use strict';
  var $ = function (id) { return document.getElementById(id); };
  var money = function (n, d) { return '$' + n.toFixed(d === undefined ? 2 : d); };
  function bind(ids, fn) {
    ids.forEach(function (id) {
      var el = $(id); if (!el) return;
      el.addEventListener('input', fn);
      el.addEventListener('change', fn);
    });
  }
  function radios(name) {
    return Array.prototype.slice.call(document.querySelectorAll('input[name="' + name + '"]'));
  }
  function radioVal(name) {
    var c = document.querySelector('input[name="' + name + '"]:checked');
    return c ? c.value : null;
  }

  /* ------------------------------------------------- 1 · POWER FACTOR ---- */
  if ($('pf-val')) {
    var PF_DIVISIONS = window.DPC_PF_DIVISIONS || [];
    var run = function () {
      var r = DPC.powerFactor($('pf-w').value, $('pf-v').value);
      var val = $('pf-val'), empty = $('pf-empty'), math = $('pf-math'), vs = $('pf-verdicts');
      if (!r) {
        val.innerHTML = '&mdash;'; empty.hidden = false; math.hidden = true; vs.innerHTML = '';
        return;
      }
      empty.hidden = true;
      val.textContent = r.pf.toFixed(1);
      math.hidden = false;
      math.textContent = r.grains + ' gr x ' + r.fps + ' fps / 1000 = ' + r.pf.toFixed(2);

      vs.innerHTML = PF_DIVISIONS.map(function (d) {
        var pass = r.pf >= d.min;
        return '<div class="dpc-verdict"><span><b>' + d.name + '</b>' +
               (d.note ? '<small>' + d.note + '</small>' : '') + '</span>' +
               '<span class="dpc-pill dpc-pill--' + (pass ? 'pass' : 'fail') + '">' +
               (pass ? 'Makes ' + d.min : 'Short by ' + (d.min - r.pf).toFixed(1)) +
               '</span></div>';
      }).join('');
    };
    bind(['pf-w', 'pf-v'], run); run();
  }

  /* ---------------------------------------------- 2 · SIGHT CORRECTION --- */
  if ($('sc-val')) {
    var scRun = function () {
      var type = radioVal('sc-type'), dir = radioVal('sc-dir');
      var isOptic = type === 'optic';
      $('sc-radius-field').hidden = isOptic;
      $('sc-click-field').hidden = !isOptic;

      var val = $('sc-val'), sub = $('sc-sub'), empty = $('sc-empty'),
          math = $('sc-math'), tiles = $('sc-tiles'), lab = $('sc-out-label');

      var reset = function () {
        val.innerHTML = '&mdash;'; empty.hidden = false;
        sub.hidden = true; math.hidden = true; tiles.hidden = true;
      };

      if (isOptic) {
        lab.textContent = 'Turret adjustment';
        var o = DPC.sightOptic($('sc-miss').value, $('sc-dist').value, $('sc-unit').value, $('sc-click').value, dir);
        if (!o) return reset();
        empty.hidden = true;
        val.innerHTML = Math.round(o.clicks) + '<span class="dpc-out-unit">clicks</span>';
        sub.hidden = false;
        sub.innerHTML = 'Dial <b>' + o.dial + '</b> &mdash; ' + o.unitLabel + ' per click.';
        tiles.hidden = false;
        tiles.innerHTML =
          '<div class="dpc-tile"><b>' + o.moa.toFixed(2) + '</b><span>MOA</span></div>' +
          '<div class="dpc-tile"><b>' + o.mil.toFixed(2) + '</b><span>MIL</span></div>' +
          '<div class="dpc-tile"><b>' + o.clicks.toFixed(1) + '</b><span>Clicks</span></div>';
        math.hidden = false;
        math.textContent = 'MOA = miss / (1.047 x yards / 100) = ' + $('sc-miss').value +
          ' / (1.047 x ' + o.yards.toFixed(2) + ' / 100) = ' + o.moa.toFixed(3);
      } else {
        lab.textContent = 'Sight movement';
        var s = DPC.sightIron($('sc-miss').value, $('sc-dist').value, $('sc-unit').value, $('sc-radius').value, dir);
        if (!s) return reset();
        empty.hidden = true;
        val.innerHTML = s.movement.toFixed(3) + '<span class="dpc-out-unit">in</span>';
        sub.hidden = false;
        sub.innerHTML = 'Move the <b>rear sight ' + s.rear + '</b>, or the <b>front sight ' + s.front + '</b>, by this amount.';
        tiles.hidden = true;
        math.hidden = false;
        math.textContent = '(sight radius x miss) / distance = (' + $('sc-radius').value + ' x ' +
          $('sc-miss').value + ') / ' + s.distIn + ' in = ' + s.movement.toFixed(4) + ' in';
      }
    };
    bind(['sc-miss', 'sc-dist', 'sc-unit', 'sc-radius', 'sc-click'], scRun);
    radios('sc-type').concat(radios('sc-dir')).forEach(function (el) {
      el.addEventListener('change', scRun);
    });
    scRun();
  }

  /* ------------------------------------------------------- 3 · CRIMP ----- */
  if ($('cr-val')) {
    var crRun = function () {
      var c = DPC.crimp($('cr-dia').value, $('cr-wall').value);
      var lvl = radioVal('cr-lvl');
      var val = $('cr-val'), sub = $('cr-sub'), empty = $('cr-empty'),
          math = $('cr-math'), rows = $('cr-rows');

      if (!c) {
        val.innerHTML = '&mdash;'; empty.hidden = false; sub.hidden = true; math.hidden = true;
        rows.innerHTML = DPC.crimpLevels.map(function (l) {
          return '<tr><td class="dpc-num">.' + l.key.split('.')[1] + '</td><td>' + l.label + '</td><td class="dpc-num">&mdash;</td></tr>';
        }).join('');
        return;
      }
      empty.hidden = true;
      var sel = c.rows.filter(function (r) { return r.key === lvl; })[0] || c.rows[2];
      val.innerHTML = sel.mouth.toFixed(4).replace(/^0/, '') + '<span class="dpc-out-unit">in</span>';
      sub.hidden = false;
      sub.innerHTML = sel.label + ' &mdash; ' + (sel.amount === 0
        ? 'no crimp, neck tension is holding the bullet.'
        : sel.amount.toFixed(3).replace(/^0/, '') + '&quot; of taper crimp.');
      math.hidden = false;
      math.textContent = 'bullet + (2 x wall) - crimp = ' + $('cr-dia').value + ' + (2 x ' +
        $('cr-wall').value + ') - ' + sel.amount.toFixed(3) + ' = ' + sel.mouth.toFixed(4);

      rows.innerHTML = c.rows.map(function (r) {
        return '<tr class="' + (r.key === sel.key ? 'is-on' : '') + '">' +
          '<td class="dpc-num">.' + r.key.split('.')[1] + '</td><td>' + r.label + '</td>' +
          '<td class="dpc-num">' + r.mouth.toFixed(4).replace(/^0/, '') + '&quot;</td></tr>';
      }).join('');
    };
    bind(['cr-dia', 'cr-wall'], crRun);
    radios('cr-lvl').forEach(function (el) { el.addEventListener('change', crRun); });
    crRun();
  }

  /* ----------------------------------------------- 4 · RELOADING COST ---- */
  if ($('rc-val')) {
    var rcRun = function () {
      var r = DPC.reloadingCost({
        projQty: $('rc-projq').value, projPrice: $('rc-projp').value,
        brassQty: $('rc-brassq').value, brassPrice: $('rc-brassp').value,
        brassUses: $('rc-uses').value,
        primerQty: $('rc-primq').value, primerPrice: $('rc-primp').value,
        powderLb: $('rc-powlb').value, charge: $('rc-charge').value, powderPrice: $('rc-powp').value,
        factoryPrice: $('rc-facp').value, factoryCount: $('rc-facc').value
      });
      var val = $('rc-val'), empty = $('rc-empty'), tiles = $('rc-tiles'),
          math = $('rc-math'), bd = $('rc-breakdown'), rows = $('rc-rows'),
          sav = $('rc-saving'), savTxt = $('rc-saving-txt');

      if (!r) {
        val.innerHTML = '&mdash;'; empty.hidden = false;
        tiles.hidden = true; math.hidden = true; bd.hidden = true; sav.hidden = true;
        return;
      }
      empty.hidden = true;
      val.textContent = money(r.per, 3);
      tiles.hidden = false;
      tiles.innerHTML =
        '<div class="dpc-tile"><b>' + money(r.per, 3) + '</b><span>Per round</span></div>' +
        '<div class="dpc-tile"><b>' + money(r.per100) + '</b><span>Per 100</span></div>' +
        '<div class="dpc-tile"><b>' + money(r.per1000) + '</b><span>Per 1,000</span></div>';
      math.hidden = false;
      math.textContent = 'powder: ' + Math.round(r.roundsPerPound) + ' rounds per pound (7000 gr / charge). ' +
        'brass divided across ' + r.brassUses + ' loading' + (r.brassUses === 1 ? '' : 's') + '.';

      bd.hidden = false;
      var line = function (n, v) {
        return '<tr><td>' + n + '</td><td class="dpc-num">' + money(v, 4) + '</td><td class="dpc-num">' + money(v * 1000) + '</td></tr>';
      };
      rows.innerHTML = line('Projectile', r.proj) + line('Brass', r.brass) +
                       line('Primer', r.primer) + line('Powder', r.powder) +
                       '<tr class="is-on"><td>Total</td><td class="dpc-num">' + money(r.per, 4) +
                       '</td><td class="dpc-num">' + money(r.per1000) + '</td></tr>';

      if (r.factory) {
        sav.hidden = false;
        var f = r.factory;
        savTxt.innerHTML = f.savingPer >= 0
          ? 'Factory works out at <b>' + money(f.per, 3) + '</b> a round. Reloading saves <b>' +
            money(f.savingPer, 3) + ' per round</b>, ' + money(f.saving1000) + ' per thousand &mdash; a ' +
            f.pct.toFixed(0) + '% saving.'
          : 'Factory is currently <b>cheaper</b> by ' + money(-f.savingPer, 3) +
            ' a round. Check your component prices.';
      } else { sav.hidden = true; }
    };
    bind(['rc-projq','rc-projp','rc-brassq','rc-brassp','rc-uses','rc-primq','rc-primp',
          'rc-powlb','rc-charge','rc-powp','rc-facp','rc-facc'], rcRun);
    rcRun();
  }
})();
